import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Contract } from './Contract'
import type { Line, LineConstructorArgs } from './Line'
import { Option } from './Option'
import { PSRecord } from './PSRecord'
import { FKType } from './SQLBuilder'
import { Signature } from './Signature'
import { Subaccount } from './Subaccount'
import { Taxcode } from './Taxcode'
import { Type } from './Type'
import { User } from './User'

type TransactionConstructorArgs = Omit<ConstructorArgs<() => Transaction>, 'lines'> & {
    lines: Line[] | LineConstructorArgs[] | null
}

export class Transaction extends BaseRecord {
    readonly recordType: string = 'transaction'

    refnum?: string
    paymeth?: string

    location?: string
    tax?: number
    paidDepositTax?: number
    nexus?: string
    reminder?: string
    channel?: string
    signedDate?: moment.Moment
    capturedDate?: moment.Moment
    private _paidDate?: moment.Moment
    cancelledDate?: moment.Moment
    protected _date?: moment.Moment

    discount?: number
    discountType?: string
    gratuity?: number
    gratuityType?: string

    private _total?: number

    lastModifiedDate?: moment.Moment

    readonly viewedDate?: moment.Moment
    readonly sentDate?: moment.Moment

    private _lines: Line[]
    private _amount: number | undefined

    constructor(data?: Partial<TransactionConstructorArgs>) {
        super(data)
        if (!data) {
            this.creator = PSApplication.me()
            this.employee = this.creator!.employee
            this.owner = this.creator!.owner
            this._lines = []
            return
        }

        this.refnum = data.refnum ?? undefined
        this.paymeth = data.paymeth ?? undefined
        this.signedDate = this.propertyFromTimestampArg(data.signed_date)
        this.capturedDate = this.propertyFromTimestampArg(data.captured_date)
        this._paidDate = this.propertyFromTimestampArg(data.paid_date)
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.discount = this.propertyFromCurrencyArg(data.discount)
        this.discountType = data.discount_type ?? undefined
        this.location = data.location ?? undefined
        this.tax = this.propertyFromNumberArg(data.tax)
        this.paidDepositTax = this.propertyFromNumberArg(data.paid_deposit_tax)
        this.nexus = data.nexus ?? undefined
        this.channel = data.channel ?? undefined
        this.reminder = data.reminder?.toString() ?? undefined
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
        this._total = this.propertyFromCurrencyArg(data.total)
        this.gratuity = this.propertyFromCurrencyArg(data.gratuity)
        this.gratuityType = data.gratuity_type ?? undefined
        this._date = this.propertyFromDateArg(data.date)
        this.viewedDate = this.propertyFromTimestampArg(data.viewed_date)
        this.sentDate = this.propertyFromTimestampArg(data.sent_date)

        const Line = Transaction.getLineConstructor()
        this._lines = (data.lines ?? []).map((line) =>
            line instanceof Line ? line : new Line(line)
        )

        this._amount = this.propertyFromCurrencyArg(data.amount)
    }

    clone(overrides?: Partial<TransactionConstructorArgs>): Transaction {
        return new Transaction({ ...this.serializeForClone(), ...overrides })
    }

    // This awful hack gets around a nasty circular dependency situtation I (Daniel) couldn't figure out.
    // Transaction wants to import Line, and Line wants to import Transaction. Since neither actually
    // needs the other until a model is instantiated, I'm getting around this by storing the Line
    // constructor on window and accessing it dynamically.
    private static getLineConstructor(): typeof Line {
        return (window as unknown as { PSLineModel: typeof Line }).PSLineModel
    }

    setVendorAndOwner(newVendor: User | undefined) {
        this.vendor = newVendor
        this.owner = newVendor
    }

    get allowSalesTax(): boolean {
        return !!this.taxcode
    }

    get total(): number | undefined {
        return this._total
    }

    get amount(): number | undefined {
        return this._amount
    }

    set amount(amount: number | undefined) {
        this._amount = amount
    }

    get taxableAmount(): number | undefined {
        return this.amount
    }

    get taxAmount(): number | undefined {
        return undefined
    }

    get discountAmount(): number | undefined {
        return undefined
    }

    get discountableTotal(): number | undefined {
        return this.total
    }

    set total(total: number | undefined) {
        this._total = total
    }

    get paidDate(): moment.Moment | undefined {
        return this._paidDate
    }

    set paidDate(paidDate: moment.Moment | undefined) {
        this._paidDate = paidDate
    }

    get lines() {
        return this._lines
    }

    set lines(lines: Line[]) {
        this._lines = lines
    }

    get date() {
        return this._date
    }

    set date(date: moment.Moment | undefined) {
        this._date = date
    }

    protected static getPerson(model: Transaction) {
        const creator = model.creator
        const employee = model.employee
        const me = PSApplication.me().id
        return me === model.client?.id
            ? (employee ?? (creator && me !== creator.id ? creator : model.vendor))
            : model.client
    }

    get person(): User | undefined {
        return Transaction.getPerson(this)
    }

    get units(): number {
        let totalUnits = 0
        for (const line of this.lines) {
            totalUnits += line.quantity !== undefined && line.quantity > 0 ? line.quantity : 1.0
        }
        return totalUnits
    }

    get weight(): number {
        let totalWeight = 0
        for (const line of this.lines) {
            let weight = line.weight !== undefined && line.weight > 0 ? line.weight : 0.1
            let qty = line.quantity !== undefined && line.quantity > 0 ? line.quantity : 1.0
            totalWeight += weight * qty
        }
        return totalWeight
    }

    protected copyLines(lines: Line[]): Line[] {
        const copy = lines != undefined ? [...lines] : []
        for (const line of copy) line.trandoc = this
        return copy
    }

    get payMethods(): Option[] {
        const map = Transaction.payMethodStrings()
        const methods = []
        // @ts-ignore
        for (const [id, label] of map.entries()) {
            methods.push(new Option(undefined, { id, label }))
        }
        return methods
    }

    static payMethodStrings(): Map<string, string> {
        const map = new Map<string, string>()
        map.set('cash', 'Cash')
        map.set('check', 'Check')
        map.set('square', 'Square')
        map.set('paypal', 'PayPal')
        map.set('venmo', 'Venmo')
        map.set('cashapp', 'Cash App')
        map.set('zelle', 'Zelle')
        map.set('applepay', 'Apple Pay')
        map.set('googlepay', 'Google Pay')
        map.set('other', 'Other')
        return map
    }

    static rateTypeStrings(): Option[] {
        const rateTypes = []
        rateTypes.push(new Option(undefined, { id: 'percent', label: 'Percent of total' }))
        rateTypes.push(new Option(undefined, { id: 'rate', label: 'Fixed amount' }))
        return rateTypes
    }

    static discountRateStrings(): Option[] {
        return [
            new Option(undefined, { id: '', label: 'None' }),
            new Option(undefined, { id: '5', label: '5%' }),
            new Option(undefined, { id: '10', label: '10%' }),
            new Option(undefined, { id: '12', label: '12%' }),
            new Option(undefined, { id: '14', label: '14%' }),
            new Option(undefined, { id: '15', label: '15%' }),
            new Option(undefined, { id: '20', label: '20%' }),
            new Option(undefined, { id: '18', label: '18%' }),
            new Option(undefined, { id: '22', label: '22%' }),
            new Option(undefined, { id: '25', label: '25%' }),
            new Option(undefined, { id: '30', label: '30%' }),
            new Option(undefined, { id: '35', label: '35%' }),
            new Option(undefined, { id: '40', label: '40%' }),
            new Option(undefined, { id: '45', label: '45%' }),
            new Option(undefined, { id: '50', label: '50%' }),
            new Option(undefined, { id: '55', label: '55%' }),
            new Option(undefined, { id: '60', label: '60%' }),
            new Option(undefined, { id: '65', label: '65%' }),
            new Option(undefined, { id: '70', label: '70%' }),
            new Option(undefined, { id: '75', label: '75%' }),
            new Option(undefined, { id: '80', label: '80%' }),
            new Option(undefined, { id: '90', label: '90%' }),
        ]
    }

    static gratuityRateStrings(): Option[] {
        return [
            new Option(undefined, { id: '', label: 'None' }),
            new Option(undefined, { id: '5', label: '5%' }),
            new Option(undefined, { id: '10', label: '10%' }),
            new Option(undefined, { id: '15', label: '15%' }),
            new Option(undefined, { id: '20', label: '20%' }),
            new Option(undefined, { id: '25', label: '25%' }),
            new Option(undefined, { id: '30', label: '30%' }),
            new Option(undefined, { id: '35', label: '35%' }),
            new Option(undefined, { id: '40', label: '40%' }),
            new Option(undefined, { id: '45', label: '45%' }),
            new Option(undefined, { id: '50', label: '50%' }),
            new Option(undefined, { id: '55', label: '55%' }),
            new Option(undefined, { id: '60', label: '60%' }),
            new Option(undefined, { id: '65', label: '65%' }),
            new Option(undefined, { id: '70', label: '70%' }),
            new Option(undefined, { id: '75', label: '75%' }),
            new Option(undefined, { id: '80', label: '80%' }),
            new Option(undefined, { id: '90', label: '90%' }),
            new Option(undefined, { id: '100', label: '100%' }),
        ]
    }

    static requestGratuityStrings(): Option[] {
        const strings = []
        strings.push(
            new Option(undefined, { id: 'always', label: 'At booking and final checkout' })
        )
        strings.push(new Option(undefined, { id: 'charge', label: 'Only at final checkout' }))
        return strings
    }

    get paidAmount(): number | undefined {
        return 0
    }

    get owedAmount(): number | undefined {
        return 0
    }

    get statusString(): string | undefined {
        return undefined
    }

    deserialize(data: any) {
        super.deserialize(data)
        if (data.client) this.client = BaseRecord.fromSerializer(data.client, User)
        if (data.form) this.form = BaseRecord.fromSerializer(data.form, Type)
        if (data.contract) this.contract = BaseRecord.fromSerializer(data.contract, Contract)
        if (data.vendor) this.vendor = BaseRecord.fromSerializer(data.vendor, User)
        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.employee) this.employee = BaseRecord.fromSerializer(data.employee, User)
        if (data.subaccount)
            this.subaccount = BaseRecord.fromSerializer(data.subaccount, Subaccount)
        if (data.record) this.record = BaseRecord.fromSerializer(data.record, PSRecord)
        if (data.signature) this.signature = BaseRecord.fromSerializer(data.signature, Signature)
        if (data.signedDate) this.signedDate = moment(data.signedDate)
        if (data.capturedDate) this.capturedDate = moment(data.capturedDate)
        if (data.taxcode) this.taxcode = BaseRecord.fromSerializer(data.taxcode, Taxcode)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
        if (data._paidDate) this._paidDate = moment(data._paidDate)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data._lines) {
            const Line = Transaction.getLineConstructor()
            this._lines = data._lines.map((line: any) => BaseRecord.fromSerializer(line, Line))
        }
    }

    static fk: FKType = {
        users: ['client', 'vendor', 'owner', 'creator', 'employee'],
        subaccounts: ['subaccount'],
        types: ['form'],
        records: ['record'],
        contracts: ['contract'],
        signatures: ['signature'],
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['client'] = this.client?.id ?? ''
        dict['form'] = this.form?.id ?? ''
        dict['contract'] = this.contract?.id ?? ''
        if (!this.id && (this.owner || this.vendor))
            dict['owner'] = this.vendor?.id ?? this.owner?.id ?? ''
        dict['vendor'] = this.vendor?.id ?? ''
        dict['subaccount'] = this.subaccount?.id ?? ''
        dict['refnum'] = this.refnum ?? ''
        dict['location'] = this.location ?? ''
        dict['paymeth'] = this.paymeth ?? ''
        dict['tax'] = this.taxcode && this.tax !== 0 ? this.tax : ''
        dict['paid_deposit_tax'] = this.paidDepositTax !== 0 ? this.paidDepositTax : ''
        dict['taxcode'] = this.taxcode?.id ?? ''
        dict['nexus'] = this.taxcode ? this.nexus : ''
        dict['channel'] = this.channel ?? ''
        dict['reminder'] = this.reminder ?? ''
        dict['employee'] = this.employee?.id ?? ''
        dict['total'] = this.total ?? 0
        dict['discount'] = this.discount ?? 0
        return dict
    }

    get client(): User | undefined {
        return this.getForeignRecord('client', User)
    }
    set client(client: User | undefined) {
        this.setForeignRecord('client', client)
    }
    get form(): Type | undefined {
        return this.getForeignRecord('form', Type)
    }
    set form(form: Type | undefined) {
        this.setForeignRecord('form', form)
    }
    get contract(): Contract | undefined {
        return this.getForeignRecord('contract', Contract)
    }
    set contract(contract: Contract | undefined) {
        this.setForeignRecord('contract', contract)
    }
    get vendor(): User | undefined {
        return this.getForeignRecord('vendor', User)
    }
    set vendor(vendor: User | undefined) {
        this.setForeignRecord('vendor', vendor)
    }
    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
    get employee(): User | undefined {
        return this.getForeignRecord('employee', User)
    }
    set employee(employee: User | undefined) {
        this.setForeignRecord('employee', employee)
    }
    get subaccount(): Subaccount | undefined {
        return this.getForeignRecord('subaccount', Subaccount)
    }
    set subaccount(subaccount: Subaccount | undefined) {
        this.setForeignRecord('subaccount', subaccount)
    }
    get record(): PSRecord | undefined {
        return this.getForeignRecord('record', PSRecord)
    }
    set record(record: PSRecord | undefined) {
        this.setForeignRecord('record', record)
    }
    get signature(): Signature | undefined {
        return this.getForeignRecord('signature', Signature)
    }
    set signature(signature: Signature | undefined) {
        this.setForeignRecord('signature', signature)
    }
    get taxcode(): Taxcode | undefined {
        return this.getForeignRecord('taxcode', Taxcode)
    }
    set taxcode(taxcode: Taxcode | undefined) {
        this.setForeignRecord('taxcode', taxcode)
    }
}
